import React, { useState, useRef, KeyboardEvent } from "react";
import { X } from "lucide-react";

interface TestEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (recipients: string[]) => void | Promise<void>;
  maxRecipients?: number;
}

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

export const TestEmailModal: React.FC<TestEmailModalProps> = ({
  isOpen,
  onClose,
  onSend,
  maxRecipients = 5,
}) => {
  const [recipients, setRecipients] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const addEmail = (email: string) => {
    const trimmed = email.trim().replace(/,+$/, "");
    if (!trimmed) return;

    if (!isValidEmail(trimmed)) {
      setInputError("Please enter a valid email address.");
      return;
    }
    if (recipients.includes(trimmed)) {
      setInputError("This email is already added.");
      return;
    }
    if (recipients.length >= maxRecipients) {
      setInputError(`You can only add up to ${maxRecipients} recipients.`);
      return;
    }

    setRecipients((prev) => [...prev, trimmed]);
    setInputValue("");
    setInputError("");
  };

  const removeRecipient = (email: string) => {
    setRecipients((prev) => prev.filter((r) => r !== email));
    setInputError("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === "Tab") {
      e.preventDefault();
      addEmail(inputValue);
    } else if (e.key === "Backspace" && !inputValue && recipients.length > 0) {
      setRecipients((prev) => prev.slice(0, -1));
    }
  };

  const handleSend = async () => {
    // Also commit any typed-but-not-added email
    let finalRecipients = [...recipients];
    if (inputValue.trim()) {
      if (!isValidEmail(inputValue.trim())) {
        setInputError("Please enter a valid email address.");
        return;
      }
      if (!finalRecipients.includes(inputValue.trim())) {
        finalRecipients = [...finalRecipients, inputValue.trim()];
        setRecipients(finalRecipients);
        setInputValue("");
      }
    }

    if (finalRecipients.length === 0) {
      setInputError("Please add at least one recipient.");
      return;
    }

    setIsSending(true);
    try {
      await onSend(finalRecipients);
      onClose();
    } finally {
      setIsSending(false);
    }
  };

  const remaining = maxRecipients - recipients.length;

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Modal */}
      <div className="relative w-full max-w-[480px] mx-4 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">Send a Test Email</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Send a test email to yourself or your team to preview the content
            before sending it to your audience.
          </p>
        </div>

        {/* Input area */}
        <div className="px-6 pb-2">
          <div
            className={`min-h-[100px] rounded-xl border px-3 py-2.5 flex flex-wrap gap-2 cursor-text transition-colors ${
              inputError
                ? "border-red-400 ring-1 ring-red-300"
                : "border-gray-200 dark:border-gray-700 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-200"
            }`}
            onClick={() => inputRef.current?.focus()}
          >
            {recipients.map((email) => (
              <span
                key={email}
                className="inline-flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-sm px-2.5 py-1 rounded-lg max-w-[220px]"
              >
                <span className="truncate">{email}</span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeRecipient(email);
                  }}
                  className="text-[#7B7B7B] font-semibold dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 shrink-0 transition-colors"
                  aria-label={`Remove ${email}`}
                >
                  <X size={13} />
                </button>
              </span>
            ))}

            {recipients.length < maxRecipients && (
              <input
                ref={inputRef}
                type="email"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value);
                  if (inputError) setInputError("");
                }}
                onKeyDown={handleKeyDown}
                onBlur={() => {
                  if (inputValue.trim()) addEmail(inputValue);
                }}
                placeholder={
                  recipients.length === 0
                    ? "Type an email and press Enter or comma..."
                    : ""
                }
                className="flex-1 min-w-[180px] outline-none text-sm text-gray-700 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 bg-transparent py-1"
              />
            )}
          </div>

          {/* Error or meta */}
          <div className="flex items-center justify-between mt-2">
            {inputError ? (
              <p className="text-xs text-red-500">{inputError}</p>
            ) : (
              <span />
            )}
            <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 ml-auto">
              Total{" "}
              <span className="font-medium text-gray-600 dark:text-gray-300">
                {recipients.length}/{maxRecipients}
              </span>{" "}
              Recipients&nbsp;&nbsp;|&nbsp;&nbsp;Send Limit:{" "}
              <span className="font-medium text-gray-600 dark:text-gray-300">{remaining} remaining</span>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-5 flex gap-3 mt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-3 border border-gray-200 dark:border-gray-700 rounded-xl text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          >
            Discard
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={isSending || (recipients.length === 0 && !inputValue.trim())}
            className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
          >
            {isSending ? "Sending…" : "Send Test Email"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestEmailModal;